import {
	Ref,
	computed,
} from '@vue/composition-api';
import { STARTABLE_STATUSES, useCache } from '@front-app/store/util/cache';
import { STATUS } from '@front-app/constants/state';
import { useAsyncStatus } from './useAsyncStatus';

type PageFetcher<T> = (pageNum: number, pageLength: number) => Promise<T[]>;

export interface PaginatedApi<T> {
	getStatusAt(renderPageNum: number): STATUS;
	getPageAt(renderPageNum: number): T[];
	fetchPagesAround(curr: number): Promise<void>;
	removeItemAtPage(item: T, pageNum: number): void;
	resetToFirstPage(opts: ResetOptions): Promise<void>;
}

export type ResetOptions = { clearBeforeFetch: true; clearAfterFetch?: false }
	| { clearBeforeFetch?: false; clearAfterFetch: true };

export interface PaginatedApiConfig {
	renderedPageLength: number;
	fetchedPageLength: number;
	numItems: Ref<number>;
}

/**
 * Wraps prefetching and slicing paged data from an API, into smaller pages for local presentation.
 *
 * NOTE: All pages are understood to be 1-indexed
 *
 * @param api The API function that returns a Promised array of elements
 * @param config Configuration to set up the expected API page length, and rendered page length
 */
export function usePaginatedApi<T>(
	api: PageFetcher<T>,
	config: PaginatedApiConfig,
): PaginatedApi<T> {
	const {
		renderedPageLength,
		fetchedPageLength,
		numItems,
	} = config;
	const cache = useCache<number, T[]>({}, () => []);
	const numPages = computed(() => Math.ceil(numItems.value / renderedPageLength));
	const getFetchPageNumForRenderPageNum = (renderPage: number): number => {
		const numRendersPerApiCall = fetchedPageLength / renderedPageLength;
		return Math.ceil(renderPage / numRendersPerApiCall);
	};
	const fetchRenderPageIfNeeded = async (renderPageNum: number) => {
		const pageNumToFetch = getFetchPageNumForRenderPageNum(renderPageNum);
		if (STARTABLE_STATUSES.includes(cache.getStatus(pageNumToFetch).value)) {
			const { track } = useAsyncStatus(cache.getStatus(pageNumToFetch));
			cache.set(pageNumToFetch, await track(api(pageNumToFetch, fetchedPageLength)));
		}
	};
	const fetchPagesAround = async (currentRenderPage: number): Promise<void> => {
		// Attempt to prefetch both forwards and backwards by a span of 3 rendered pages to maintain a buffer
		// If we don't have enough buffer for those rendered pages, fetch another large API page
		const start = Math.max(currentRenderPage - 3, 1);
		const end = Math.min(currentRenderPage + 3, numPages.value);
		const promises = [];
		for (let i = start; i <= end; i++) {
			promises.push(fetchRenderPageIfNeeded(i));
		}
		await Promise.all(promises);
	};
	const getPageAt = (renderPageNum: number) => {
		const fetchPageNum = getFetchPageNumForRenderPageNum(renderPageNum);
		const startIdx = ((renderPageNum - 1) * renderedPageLength) - ((fetchPageNum - 1) * fetchedPageLength);
		return cache.get(fetchPageNum).slice(
			startIdx,
			startIdx + renderedPageLength,
		);
	};
	const getStatusAt = (renderPageNum: number) => {
		const fetchPageNum = getFetchPageNumForRenderPageNum(renderPageNum);
		return cache.getStatus(fetchPageNum).value;
	};
	const removeItemAtPage = (item: T, renderPageNum: number) => {
		const fetchPageNum = getFetchPageNumForRenderPageNum(renderPageNum);
		cache.set(fetchPageNum, cache.get(fetchPageNum).filter((candidate) => candidate !== item));
	};
	const resetToFirstPage = async (options: ResetOptions): Promise<void> => {
		if (options.clearBeforeFetch) {
			cache.clear();
			await fetchPagesAround(1);
			return;
		}
		if (options.clearAfterFetch) {
			const { track } = useAsyncStatus(cache.getStatus(1));
			const results = await track(api(1, fetchedPageLength));
			cache.clear();
			cache.set(1, results);
		}
	};
	return {
		getStatusAt,
		getPageAt,
		fetchPagesAround,
		removeItemAtPage,
		resetToFirstPage,
	};
}
